import { Link } from "react-router";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import {
  useBreadCrumbState,
  useCategoriesFromPath,
} from "@components/Header/helpers";
import BtLuxeCarousel from "@components/Slider/BtLuxeCarousel";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import LifestyleHero from "@routes/Section/components/Lifestyle/LifestyleHero";
import LifestyleNews from "@routes/Section/components/Lifestyle/LifestyleNews";
import SectionDivider from "@routes/Section/components/Lifestyle/SectionDivider";
import { LifestylePageData } from "@routes/Section/layouts/Lifestyle/utils/types";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";

/**
 * Content component for lifestyle page.
 * */
const LifestylePageContent = ({
  lifestyle,
  lifestyleCarousel,
  seoFooterData,
}: LifestylePageData): React.ReactElement => {
  const { breadcrumbsRef, isShowBreadCrumb } = useBreadCrumbState();
  const { parentCategory, childCategory } = useCategoriesFromPath();

  return (
    <div className="w-full">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <PulseEntry />

      <Header
        displayBreadcrumb={
          parentCategory &&
          sectionNavigationItems?.[parentCategory] &&
          isShowBreadCrumb
            ? true
            : false
        }
        parentCategory={parentCategory}
        childCategory={childCategory}
        categoryDataMapping={sectionNavigationItems}
      />

      {parentCategory && sectionNavigationItems?.[parentCategory] ? (
        <div className="flex-none">
          <Container>
            <h1 className="pb-3 pt-6 font-playfair text-6xl font-semibold uppercase text-gray-850 md:text-8xl">
              Lifestyle
            </h1>

            <div ref={breadcrumbsRef}>
              <Breadcrumb
                className="container m-auto"
                parentCategory={parentCategory}
                childCategory={childCategory}
                categoryDataMapping={sectionNavigationItems}
                extraBreadcrumbItems={
                  <div
                    className={cn(
                      "relative box-border flex shrink-0 items-center border-r border-gray-175 pr-3 font-poppins text-sm font-bold tracking-tightest text-gray-850 hover:opacity-80"
                    )}
                  >
                    <Link
                      to={`${RouteFactory.lifestyle}?ref=listing-menubar`}
                      reloadDocument
                    >
                      Top stories
                    </Link>
                  </div>
                }
              />
            </div>
          </Container>
        </div>
      ) : null}

      {lifestyle[0] ? <LifestyleHero articleProps={lifestyle[0]} /> : null}

      {lifestyle.length ? <SectionDivider /> : null}

      <Container>
        <LifestyleNews cardProps={lifestyle.slice(1)} />

        <BtLuxeCarousel
          cardProps={lifestyleCarousel}
          dotsCustomClass="lifestyle-carousel-dots"
          btLuxeClass="self-center lg:self-start"
        />
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default LifestylePageContent;
